import { inject, Injectable, Signal, signal } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ObjectHelper, StringHelper } from '@newroom-connect/library/helpers';
import { ITokenSet } from '@newroom-connect/library/interfaces';
import { UserSession } from '@newroom-connect/library/models';

import { LocalStorageService } from '../local-storage/local-storage.service';
import { LoggingService } from '../logging/logging.service';

export const SessionChangeEventType = {
  SESSION_CLEARED: 'SESSION_CLEARED',
  SESSION_SET: 'SESSION_SET',
  SESSION_INITIALIZED: 'SESSION_INITIALIZED'
} as const;

export type SessionChangeEventType = typeof SessionChangeEventType[keyof typeof SessionChangeEventType];

export interface SessionChangeEvent {
  type: SessionChangeEventType;
  userSession: UserSession | null;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly logger = inject(LoggingService);

  private static readonly LOCAL_STORAGE_KEYS = ['accessToken', 'idToken', 'refreshToken', 'rememberMe', 'redirectArea', 'sessionId', 'username', 'isCodeLogin', 'isPreviewMode'];

  private userSession = signal<UserSession | null>(null);
  private isCodeLoginInProgress = signal<boolean>(false);

  private sessionChangeSubject$ = new Subject<SessionChangeEvent>();

  /**
   * Watch session change events.
   *
   * @returns Observable of session change events.
   */
  public watchSessionChanges(): Observable<SessionChangeEvent> {
    return this.sessionChangeSubject$.asObservable();
  }

  /**
   * Watch the code login progress state.
   *
   * @returns Read-only signal of the code login progress state.
   */
  public watchCodeLoginProgress(): Signal<boolean> {
    return this.isCodeLoginInProgress.asReadonly();
  }

  /**
   * Set the code login progress state.
   *
   * @param value The new state value.
   */
  public setCodeLoginProgress(value: boolean): void {
    this.isCodeLoginInProgress.set(value);
  }

  /**
   * Initializes the session service where the token set from the local storage is loaded into the user session.
   */
  public initialize(): void {
    const accessToken = StringHelper.normalizeOptionalString(this.localStorageService.get('accessToken'));
    const idToken = StringHelper.normalizeOptionalString(this.localStorageService.get('idToken'));
    const refreshToken = StringHelper.normalizeOptionalString(this.localStorageService.get('refreshToken'));
    const sessionId = StringHelper.normalizeOptionalString(this.localStorageService.get('sessionId')) ?? '';
    const username = StringHelper.normalizeOptionalString(this.localStorageService.get('username'));
    const redirectArea = StringHelper.normalizeOptionalString(this.localStorageService.get('redirectArea'));
    const rememberMe = ObjectHelper.parseBoolean(this.localStorageService.get('rememberMe'));
    const isPreviewMode = ObjectHelper.parseBoolean(this.localStorageService.get('isPreviewMode'));
    const isCodeLogin = ObjectHelper.parseBoolean(this.localStorageService.get('isCodeLogin'));

    // If no session ID is available, do not initialize the session.
    if (!sessionId || !username || !accessToken || !idToken || !refreshToken) {
      this.logger.warn('Session data is incomplete');
      return;
    }

    const newSession = new UserSession(
      {
        accessToken,
        idToken,
        refreshToken
      },
      sessionId,
      username,
      redirectArea,
      rememberMe,
      isPreviewMode,
      isCodeLogin
    );

    this.userSession.set(newSession);
    this.sessionChangeSubject$.next({ type: SessionChangeEventType.SESSION_INITIALIZED, userSession: newSession });
  }

  /**
   * Watch the current user session.
   *
   * @returns Read-only signal of the current user session.
   */
  public watchUserSession(): Signal<UserSession | null> {
    return this.userSession.asReadonly();
  }

  /**
   * Get the current user session.
   *
   * @returns The current user session or `null` if no session is available.
   */
  public getUserSession(): UserSession | null {
    return this.userSession();
  }

  /**
   * Set tokens from a given token set in the local storage and the current token set.
   *
   * @param tokenSet The token set to set.
   * @param sessionId
   * @param username
   * @param rememberMe Indicator, if "rememberMe" is desired -> will store tokens in local storage.
   * @param redirectArea Redirect area slug if available.
   * @param isPreviewMode Indicator if the session is in preview mode.
   * @param isCodeLogin Indicator if the session is in code login mode.
   */
  public setUserSession(tokenSet: Partial<ITokenSet>, sessionId: string, username: string, rememberMe?: boolean, redirectArea: string | null = null, isPreviewMode = false, isCodeLogin = false): void {
    if (rememberMe) {
      if (tokenSet.accessToken) {
        this.localStorageService.set('accessToken', tokenSet.accessToken);
      }

      if (tokenSet.idToken) {
        this.localStorageService.set('idToken', tokenSet.idToken);
      }

      if (tokenSet.refreshToken) {
        this.localStorageService.set('refreshToken', tokenSet.refreshToken);
      }

      if (username) {
        this.localStorageService.set('username', username);
      }

      if (isCodeLogin) {
        this.localStorageService.set('isCodeLogin', `${isCodeLogin ? true : false}`);
      }

      if (redirectArea) {
        this.localStorageService.set('redirectArea', redirectArea);
      }

      this.localStorageService.set('sessionId', sessionId);
      this.localStorageService.set('rememberMe', `${rememberMe ? true : false}`);
    }

    const newSession = new UserSession(tokenSet, sessionId, username, redirectArea, rememberMe, isPreviewMode, isCodeLogin);

    this.userSession.set(newSession);
    this.sessionChangeSubject$.next({ type: SessionChangeEventType.SESSION_SET, userSession: newSession });
  }

  /**
   * Remove existing tokens from the local storage and the current token set.
   */
  public clearUserSession(): void {
    for (const localStorageKey of SessionService.LOCAL_STORAGE_KEYS) {
      this.localStorageService.remove(localStorageKey);
    }

    this.userSession.set(null);
    this.sessionChangeSubject$.next({ type: SessionChangeEventType.SESSION_CLEARED, userSession: null });
  }
}
